import { useState, useEffect } from "react";
import { Button, SMS_SOFT_LIMIT } from "@components";
import { formatAmount } from "@utils";
import { invoiceLabels } from "@components/paymentTemplates/TemplateTile";
import InlineCurrencyInput from "./InlineCurrencyInput";

export default function PreviewFooter({
  previewMode,
  isCustom,
  accountLabel,
  control,
  customAmountError,
  invoicesRequested,
  totalAmount,
  smsEnabled,
  emailEnabled,
  isEdited,
  hasUnappliedChanges,
  getValues,
  smsText,
  onBack,
  hideBack = false,
  onReview,
  onCancelEdit,
  onResetInEditMode,
  onSaveAsTemplate,
  canEditTemplates = true,
  onDoneEdit,
  emailSubject,
  readOnly = false,
  onClose,
}) {
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    if (validationError) setValidationError(null);
  }, [smsText, emailSubject, previewMode, smsEnabled, emailEnabled]);

  const checkValidation = () => {
    if (emailEnabled) {
      const subject = getValues("emailSubject") || "";
      if (!subject.trim()) {
        setValidationError("Email subject line is required");
        return false;
      }
    }
    if (smsEnabled) {
      const smsText = getValues("smsText") || "";
      if (smsText.length > SMS_SOFT_LIMIT) {
        const over = smsText.length - SMS_SOFT_LIMIT;
        setValidationError(
          `SMS exceeds ${SMS_SOFT_LIMIT} character limit by ${over} ${over === 1 ? "character" : "characters"}`,
        );
        return false;
      }
    }
    setValidationError(null);
    return true;
  };

  const guardAction = (action) => () => {
    if (!checkValidation()) return;
    action();
  };

  if (readOnly) {
    return (
      <div className="shrink-0 border-t border-zinc-200 bg-white">
        <div className="p-4 flex items-center justify-end">
          <Button onClick={onClose} color="secondary">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-t border-zinc-200 bg-white">
      {previewMode && isCustom && (
        <div className="px-4 pt-3 text-sm flex flex-col items-end text-zinc-600">
          <span className="mr-2">
            Custom amount ({accountLabel}) &nbsp;&nbsp;&nbsp;
            <InlineCurrencyInput name="customAmount" control={control} />
          </span>
          {customAmountError && (
            <span className="mr-2 mt-1 text-xs text-red-500">
              {customAmountError}
            </span>
          )}
        </div>
      )}

      {previewMode && !isCustom && invoicesRequested !== "None" && (
        <div className="px-4 pt-3 text-sm flex justify-end items-end text-zinc-600">
          <span className="mr-2">
            {`${invoiceLabels[invoicesRequested]} (${accountLabel})`}
            &nbsp;&nbsp;&nbsp;
            <span className="font-semibold text-zinc-900 text-2xl">
              {formatAmount(totalAmount)}
            </span>
          </span>
        </div>
      )}

      {validationError && (
        <div className="px-4 pt-2 text-xs text-red-500 text-right">
          {validationError}
        </div>
      )}

      <div className="p-4 flex items-center gap-2">
        {previewMode ? (
          <>
            {!hideBack && (
              <Button onClick={onBack} color="secondary">
                Back to Templates
              </Button>
            )}
            <Button
              color="primary"
              className="ml-auto"
              onClick={guardAction(onReview)}
              disabled={!smsEnabled && !emailEnabled}
            >
              Review
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onCancelEdit} color="secondary">
              Cancel
            </Button>
            <div className="ml-auto flex items-center gap-2">
              {isEdited && (
                <>
                  <Button
                    onClick={() => {
                      setValidationError(null);
                      onResetInEditMode();
                    }}
                    color="secondaryLink"
                  >
                    Reset
                  </Button>
                  {canEditTemplates && (
                    <Button
                      onClick={guardAction(onSaveAsTemplate)}
                      color="secondary"
                    >
                      Save as new Template
                    </Button>
                  )}
                </>
              )}
              <Button
                color="primary"
                onClick={guardAction(onDoneEdit)}
                disabled={!hasUnappliedChanges}
              >
                Apply changes
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
